/**
 * Brand Plane Bridge — Business plane WCAG validation
 * @see docs/inflomatrix-constitution/ui-architecture/BRAND_PLANE_BRIDGE.md §12-A
 */

import {
  BUSINESS_PLANE_CONTRAST_SURFACES,
  type BrandIdentity,
  clampAccentForBusinessPlane,
  computeAccentOn,
  contrastRatio,
  WCAG_AA_CONTRAST,
} from '@shared/site/design/brand-identity';
import { isForbiddenBusinessBrandToken } from '@shared/site/design/brand-injection-tier-registry';

/** §8 Studio warning copy — keep in sync with BRAND_PLANE_BRIDGE.md */
export const BRAND_ACCENT_CLAMP_WARNING_KO =
  '이 색상은 Business dark mode에서 접근성 기준을 맞추기 위해 약간 보정됩니다. Site 공개 페이지에는 선택한 원색이 그대로 적용됩니다.';

export interface BrandPlaneValidationIssue {
  code: 'accent_on_contrast' | 'accent_surface_contrast' | 'forbidden_token';
  message: string;
}

export interface BrandPlaneValidationResult {
  valid: boolean;
  identity: BrandIdentity;
  issues: BrandPlaneValidationIssue[];
}

function identityRecordKeys(identity: BrandIdentity): string[] {
  return Object.keys(identity).filter((key) => identity[key as keyof BrandIdentity] !== undefined);
}

/** Validate (and lightly repair) BrandIdentity for Business dark ERP consumption */
export function validateBrandForBusinessPlane(identity: BrandIdentity): BrandPlaneValidationResult {
  const issues: BrandPlaneValidationIssue[] = [];
  let working: BrandIdentity = { ...identity };

  for (const key of identityRecordKeys(working)) {
    if (isForbiddenBusinessBrandToken(key)) {
      issues.push({
        code: 'forbidden_token',
        message: `Forbidden Business brand field: ${key}`,
      });
    }
  }

  const { accent, wasClamped } = clampAccentForBusinessPlane(working.accent);
  if (wasClamped) {
    working = {
      ...working,
      accent,
      accentOn: computeAccentOn(accent),
      accentWasClamped: true,
    };
  }

  if (contrastRatio(working.accentOn, working.accent) < WCAG_AA_CONTRAST) {
    working = { ...working, accentOn: computeAccentOn(working.accent) };
  }

  const accentOnValid = contrastRatio(working.accentOn, working.accent) >= WCAG_AA_CONTRAST;
  if (!accentOnValid) {
    issues.push({
      code: 'accent_on_contrast',
      message: 'accentOn contrast below WCAG AA against accent background',
    });
  }

  const failingSurfaces = BUSINESS_PLANE_CONTRAST_SURFACES.filter(
    (surface) => contrastRatio(working.accent, surface) < WCAG_AA_CONTRAST
  );
  if (failingSurfaces.length > 0) {
    issues.push({
      code: 'accent_surface_contrast',
      message: `Accent contrast below WCAG AA on surfaces: ${failingSurfaces.join(', ')}`,
    });
  }

  const noForbidden = !issues.some((issue) => issue.code === 'forbidden_token');
  const accentSurfaceValid = failingSurfaces.length === 0;

  return {
    valid: accentOnValid && accentSurfaceValid && noForbidden,
    identity: working,
    issues,
  };
}
